import React, { useState, useEffect, useCallback } from "react";
import Input from "../../../components/ui/input";
import Button from "../../../components/ui/button";
import Table from "../../../components/ui/table";
import Checkbox from "../../../components/ui/checkbox";
import styles from "./reminders.module.css";
import Modal from "../../../components/ui/modal";
import { useParams, useOutletContext } from "react-router-dom";
import { showSuccessToast, showErrorToast, notifications } from "../../../utils/notifications";
import { formatDateToSpanish } from "../../../utils/dateUtils";
import { 
  getRemindersSummary, 
  createReminder, 
  updateReminder, 
  deleteReminder 
} from "../../../services/apiService";
import usePageTitle from "../../../hooks/usePageTitle";
import UnsavedChangesModal from "../../../components/modals/unsaved-changes-modal";
import useUnsavedChangesWarning from "../../../hooks/useUnsavedChangesWarning";

const Reminders = () => {
  const { id } = useParams();
  usePageTitle(`Expediente ${id}`, 'Recordatorios');
  const { isLockedByOther } = useOutletContext();
  const [isReminderModalOpen, setIsReminderModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [remindersData, setRemindersData] = useState([]);
  const [selectedReminder, setSelectedReminder] = useState(null);
  const [originalReminderData, setOriginalReminderData] = useState(null);
  const [reminderFormData, setReminderFormData] = useState({
    name: "",
    date: "",
    description: "",
  });
  const [editFormData, setEditFormData] = useState({
    nombre: "",
    fecha: "",
    observaciones: "",
  });
  const [finalizarRecordatorioChecked, setFinalizarRecordatorioChecked] = useState(false);

  // Estados para detectar cambios sin guardar
  const [initialReminderFormData, setInitialReminderFormData] = useState(null);
  const [initialEditFormData, setInitialEditFormData] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Hook para advertir cambios sin guardar
  const { showModal, handleCancel, handleConfirm, message } = useUnsavedChangesWarning(
    hasUnsavedChanges,
    "Tienes cambios sin guardar en el recordatorio."
  );

  // Función para obtener el usuarioId del localStorage
  const getUsuarioId = () => {
    return localStorage.getItem("usuarioId");
  };

  // useEffect para detectar cambios sin guardar
  useEffect(() => {
    if (isReminderModalOpen && initialReminderFormData) {
      const reminderFormChanged = JSON.stringify(reminderFormData) !== JSON.stringify(initialReminderFormData);
      setHasUnsavedChanges(reminderFormChanged);
    } else if (isEditModalOpen && initialEditFormData) {
      const editFormChanged = JSON.stringify(editFormData) !== JSON.stringify(initialEditFormData) || 
                             finalizarRecordatorioChecked !== (selectedReminder?.finalizarRecordatorio || false);
      setHasUnsavedChanges(editFormChanged);
    } else {
      setHasUnsavedChanges(false);
    }
  }, [reminderFormData, isReminderModalOpen, initialReminderFormData, editFormData, isEditModalOpen, initialEditFormData, finalizarRecordatorioChecked, selectedReminder]);

  const handleReminderClick = (reminder) => {
    console.log("Reminder clicked:", reminder);
    
    // Convertir fecha a formato YYYY-MM-DD para el input date
    let dateForInput = reminder.originalDate;
    if (reminder.originalDate) {
      // Si la fecha viene en formato ISO, extraer solo la parte de la fecha
      if (reminder.originalDate.includes('T')) {
        dateForInput = reminder.originalDate.split('T')[0];
      } else {
        // Si ya está en formato YYYY-MM-DD, usarla directamente
        dateForInput = reminder.originalDate;
      }
    }
    
    setSelectedReminder(reminder);
    const initialEdit = {
      nombre: reminder.name,
      fecha: dateForInput,
      observaciones: reminder.description || "",
    };
    setEditFormData(initialEdit);
    setInitialEditFormData(initialEdit);
    // Prellenar el checkbox según venga desde la API
    setFinalizarRecordatorioChecked(!!reminder.finalizarRecordatorio);
    setOriginalReminderData({
      nombre: reminder.name,
      fecha: dateForInput,
      observaciones: reminder.description || "",
    });
    setIsEditModalOpen(true);
  };

  // Cargar recordatorios
  const loadReminders = useCallback(async () => {
    try {
      const data = await getRemindersSummary(id);
      const mappedData = data.map(item => ({
        id: item.id,
        name: item.nombre,
        date: formatDateToSpanish(item.fecha),
        originalDate: item.fecha,
        description: item.descripcion || "",
        // Mapeamos el flag recibido; puede ser true/false/null
        finalizarRecordatorio: item.finalizarRecordatorio,
        // Campo de display para la tabla
        finalizarRecordatorioDisplay: item.finalizarRecordatorio ? 'Finalizado' : 'Sin finalizar',
      }));
      setRemindersData(mappedData);
    } catch (error) {
      console.error("Error loading reminders:", error);
      showErrorToast("Error al cargar los recordatorios");
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      loadReminders();
    }
  }, [id, loadReminders]);

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const hasChanges = () => {
    if (!originalReminderData) return false;
    return (
      editFormData.nombre !== originalReminderData.nombre ||
      editFormData.fecha !== originalReminderData.fecha ||
      editFormData.observaciones !== originalReminderData.observaciones
    );
  };

  const handleUpdateReminder = async () => {
    // Allow update also when finalizar checkbox is checked even if no other changes
    if (!selectedReminder || (!hasChanges() && !finalizarRecordatorioChecked)) return;
    
    // Validaciones básicas
    if (!editFormData.nombre?.trim()) {
      showErrorToast("Por favor ingresa un nombre para el recordatorio");
      return;
    }
    
    if (!editFormData.fecha) {
      showErrorToast("Por favor selecciona una fecha");
      return;
    }
    
    try {
      const updateData = {
        expedienteId: parseInt(id),
        nombre: editFormData.nombre,
        fecha: editFormData.fecha, // Enviar fecha directamente en formato YYYY-MM-DD
        descripcion: editFormData.observaciones,
        // Si el usuario marca finalizarRecordatorio, enviar true
        ...(finalizarRecordatorioChecked ? { finalizarRecordatorio: true } : {}),
      };
      
      console.log("Updating reminder with data:", updateData);
      await updateReminder(selectedReminder.id, updateData);
      showSuccessToast(notifications.update.success("Recordatorio"));
      
      setIsEditModalOpen(false);
      setFinalizarRecordatorioChecked(false);
      setInitialEditFormData(null);
      setHasUnsavedChanges(false);
      loadReminders(); // Recargar la lista
    } catch (error) {
      console.error("Error updating reminder:", error);
      showErrorToast(error.response?.data?.message || notifications.update.error("recordatorio"));
    }
  };

  const handleDeleteReminder = async () => {
    if (!selectedReminder) return;
    
    if (window.confirm("¿Estás seguro de que quieres eliminar este recordatorio?")) {
      try {
        await deleteReminder(selectedReminder.id);
        showSuccessToast(notifications.delete.success("Recordatorio"));
        setIsEditModalOpen(false);
        loadReminders(); // Recargar la lista
      } catch (error) {
        console.error("Error deleting reminder:", error);
        showErrorToast(error.response?.data?.message || notifications.delete.error("recordatorio"));
      }
    }
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedReminder(null);
    setOriginalReminderData(null);
    setFinalizarRecordatorioChecked(false);
    setInitialEditFormData(null);
    setHasUnsavedChanges(false);
  };

  const handleReminderFormChange = (e) => {
    const { name, value } = e.target;
    console.log(`Form field changed: ${name} = ${value}`);
    setReminderFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetReminderForm = () => {
    setReminderFormData({
      name: "",
      date: "",
      description: "",
    });
  };

  const handleCloseModal = () => {
    setIsReminderModalOpen(false);
    setInitialReminderFormData(null);
    setHasUnsavedChanges(false);
    resetReminderForm();
  };

  const handleOpenModal = () => {
    // Inicializar con campos vacíos cuando se abre el modal
    const initialReminder = {
      name: "",
      date: "",
      description: "",
    };
    setReminderFormData(initialReminder);
    setInitialReminderFormData(initialReminder);
    setIsReminderModalOpen(true);
  };

  const handleReminderSubmit = async (e) => {
    e.preventDefault();
    
    // Validaciones básicas
    if (!reminderFormData.name?.trim()) {
      showErrorToast("Por favor ingresa un nombre para el recordatorio");
      return;
    }
    
    if (!reminderFormData.date) {
      showErrorToast("Por favor selecciona una fecha");
      return;
    }
    
    console.log("Form data before submit:", reminderFormData);
    
    try {
      const reminderData = {
        usuarioId: getUsuarioId(), // Agregar el usuarioId del localStorage
        expedienteId: parseInt(id),
        nombre: reminderFormData.name,
        fecha: reminderFormData.date, // Enviar fecha directamente en formato YYYY-MM-DD
        descripcion: reminderFormData.description,
      };
      
      console.log("Sending reminder data:", reminderData);
      await createReminder(reminderData);
      showSuccessToast(notifications.create.success("Recordatorio"));

      setIsReminderModalOpen(false);
      setInitialReminderFormData(null);
      setHasUnsavedChanges(false);
      resetReminderForm();
      loadReminders(); // Recargar la lista
    } catch (error) {
      console.error("Error creating reminder:", error);
      showErrorToast(error.response?.data?.message || notifications.create.error("recordatorio"));
    }
  };

  const remindersTableHeaders = [
    { label: "Nombre", key: "name" },
    { label: "Fecha", key: "date" },
    { label: "Descripción", key: "description" },
    { label: "Estado", key: "finalizarRecordatorioDisplay" },
  ];

  return (
    <div style={{ paddingTop: "24px" }}>
      <div className={styles.tableContainer}>
        <Table
          headers={remindersTableHeaders}
          data={remindersData}
          onPress={isLockedByOther ? null : handleReminderClick}
        />
      </div>

      <Button onClick={handleOpenModal} disabled={isLockedByOther}>
        NUEVO RECORDATORIO
      </Button>

      {/* Modal para crear nuevo recordatorio */}
      <Modal
        isOpen={isReminderModalOpen}
        onClose={handleCloseModal}
        title="Nuevo Recordatorio"
      >
        <form onSubmit={handleReminderSubmit} className={styles.reminderForm}>
          <Input
            id="name"
            name="name"
            value={reminderFormData.name}
            onChange={handleReminderFormChange}
            placeholder="Nombre del recordatorio"
            required
            style={{ width: "100%" }}
          />

          <Input
            id="date"
            name="date"
            type="date"
            value={reminderFormData.date}
            onChange={handleReminderFormChange}
            required
            style={{ width: "100%" }}
          />

          <textarea
            id="description"
            name="description"
            value={reminderFormData.description}
            onChange={handleReminderFormChange}
            placeholder="Escribe la descripción aquí..."
            className={styles.reminderDescription}
            rows={5}
            spellCheck="true"
          />

          <Button
            type="submit"
            style={{ width: "fit-content", alignSelf: "center" }}
          >
            Guardar
          </Button>
        </form>
      </Modal>

      {/* Modal para editar recordatorio */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        title="Editar Recordatorio"
      >
        <div className={styles.reminderForm}>
          <Input
            id="nombre"
            name="nombre"
            value={editFormData.nombre}
            onChange={handleEditFormChange}
            placeholder="Nombre del recordatorio"
            required
            style={{ width: "100%" }}
          />

          <Input
            id="fecha"
            name="fecha"
            type="date"
            value={editFormData.fecha}
            onChange={handleEditFormChange}
            required
            style={{ width: "100%" }}
          />

          <textarea
            id="observaciones"
            name="observaciones"
            value={editFormData.observaciones}
            onChange={handleEditFormChange}
            placeholder="Escribe las observaciones aquí..."
            className={styles.reminderDescription}
            rows={5}
            spellCheck="true"
          />

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px' }}>
            <Checkbox
              label="Finalizar Recordatorio"
              checked={finalizarRecordatorioChecked}
              onChange={(checked) => { if (!isLockedByOther) setFinalizarRecordatorioChecked(checked); }}
              style={isLockedByOther ? { opacity: 0.6, pointerEvents: 'none' } : {}}
            />
          </div>

          <div style={{ display: "flex", gap: "12px", justifyContent: "center", marginTop: "16px" }}>
            <Button
              onClick={handleUpdateReminder}
              disabled={(!hasChanges() && !finalizarRecordatorioChecked) || isLockedByOther}
              style={{ width: "fit-content" }}
            >
              Guardar
            </Button>
            <Button
              onClick={handleDeleteReminder}
              disabled={isLockedByOther}
              style={{ 
                width: "fit-content", 
                backgroundColor: "var(--red)",
                borderColor: "var(--red)"
              }}
            >
              Eliminar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal de advertencia de cambios sin guardar */}
      <UnsavedChangesModal
        isOpen={showModal}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
        message={message}
      />
    </div>
  );
};

export default Reminders;
